/* SIZESOFTX
=========================================================================================
* Descripción: Liberación de licencias y equipos activados desde Support SizeSoftX
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 12/09/2026 | José Luis Aragón Cervantes       | Creación
*      2       | 15/09/2026 | José Luis Aragón Cervantes       | Muestra error de liberación junto al botón
* ---------------------------------------------------------------------------------------
*/

import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  DeleteForever as DeleteForeverIcon,
  MailOutline as MailOutlineIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import {
  apiLicenciaLiberacionDetalle,
  apiLicenciaLiberar,
  apiLicenciasLiberacionBuscar,
  apiValidacionTitularidadConfirmarLiberacion,
  apiValidacionTitularidadEnviar,
} from "../settings/supportApi";
import {
  EMAIL_REGEX,
  sanitizarEmail,
  sanitizarTexto,
  sanitizarMultilinea,
  validarSinInyeccion,
} from "../settings/inputSecurity";

const formatFecha = (v) => (v ? new Date(v).toLocaleString("es-MX") : "—");
const show = (v) => (v ? v : "—");

const ImpactoItem = ({ label, value, color = "default" }) => (
  <Chip size="small" color={color} variant={color === "default" ? "outlined" : "filled"} label={`${label}: ${value || 0}`} />
);

const LiberacionLicenciaPage = () => {
  const [busqueda, setBusqueda] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [liberarError, setLiberarError] = useState("");
  const [correoTo, setCorreoTo] = useState("");
  const [correoMensaje, setCorreoMensaje] = useState("");
  const [codigoValidacion, setCodigoValidacion] = useState("");
  const [validacionExpiresAt, setValidacionExpiresAt] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [confirmacion, setConfirmacion] = useState("");
  const [titularidadValidada, setTitularidadValidada] = useState(false);
  const [eliminarBaseFisica, setEliminarBaseFisica] = useState(false);
  const [working, setWorking] = useState(false);

  const limpiarMensajes = () => {
    setError("");
    setOk("");
    setLiberarError("");
  };

  const buscar = async () => {
    limpiarMensajes();
    const termino = sanitizarTexto(busqueda, 120).trim();
    if (!termino) {
      setError("Indica licencia, TenantKey, base de datos, razón social, correo, RFC o equipo para buscar.");
      return;
    }
    const bloqueo = validarSinInyeccion([{ etiqueta: "Búsqueda", valor: busqueda }]);
    if (bloqueo) {
      setError(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }

    setLoading(true);
    try {
      const data = await apiLicenciasLiberacionBuscar({ busqueda: termino, limite: 50 });
      setRows(data || []);
      if (!data?.length) {
        setDetalle(null);
        setOk("No se encontraron coincidencias.");
      }
    } catch (err) {
      setError(err.message || "No fue posible buscar la licencia.");
    } finally {
      setLoading(false);
    }
  };

  const seleccionar = async (tenantActivationLicenseId) => {
    limpiarMensajes();
    setDetalleLoading(true);
    try {
      const data = await apiLicenciaLiberacionDetalle(tenantActivationLicenseId);
      setDetalle(data);
      setCorreoTo(data?.correoValidacionSugerido || "");
      setCorreoMensaje("");
      setCodigoValidacion("");
      setValidacionExpiresAt(null);
      setMotivo("");
      setConfirmacion("");
      setTitularidadValidada(Boolean(data?.ownershipVerifiedAtUtc));
      setEliminarBaseFisica(false);
    } catch (err) {
      setError(err.message || "No fue posible cargar el detalle de la licencia.");
    } finally {
      setDetalleLoading(false);
    }
  };

  const enviarCorreo = async () => {
    if (!detalle) return;
    limpiarMensajes();
    const destino = sanitizarEmail(correoTo, 180);
    const mensaje = sanitizarMultilinea(correoMensaje, 1000);
    if (destino && !EMAIL_REGEX.test(destino)) {
      setError("Correo destinatario inválido.");
      return;
    }
    const bloqueo = validarSinInyeccion([
      { etiqueta: "Destinatario", valor: correoTo },
      { etiqueta: "Mensaje", valor: correoMensaje },
    ]);
    if (bloqueo) {
      setError(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }
    setWorking(true);
    try {
      const data = await apiValidacionTitularidadEnviar({
        tenantId: detalle.tenantId || null,
        tenantActivationLicenseId: detalle.tenantActivationLicenseId,
        toEmail: destino || undefined,
        mensaje: mensaje || undefined,
      });
      setCodigoValidacion("");
      setTitularidadValidada(false);
      setValidacionExpiresAt(data?.expiresAtUtc || null);
      setOk(data?.mensaje || "Correo de validación enviado.");
    } catch (err) {
      setError(err.message || "No fue posible enviar el correo de validación.");
    } finally {
      setWorking(false);
    }
  };

  const confirmarCodigo = async () => {
    if (!detalle) return;
    limpiarMensajes();
    if (!codigoValidacion.trim()) {
      setError("Captura el código recibido por correo.");
      return;
    }

    setWorking(true);
    try {
      const data = await apiValidacionTitularidadConfirmarLiberacion({
        tenantActivationLicenseId: detalle.tenantActivationLicenseId,
        codigo: codigoValidacion.trim(),
      });
      if (data?.confirmado) {
        setTitularidadValidada(true);
        setDetalle({ ...detalle, ownershipVerifiedAtUtc: data.verifiedAtUtc || new Date().toISOString() });
        setOk(data?.mensaje || "Titularidad confirmada.");
      } else {
        setError(data?.mensaje || "No se pudo confirmar el código.");
      }
    } catch (err) {
      setError(err.message || "No fue posible confirmar el código.");
    } finally {
      setWorking(false);
    }
  };

  const liberar = async () => {
    if (!detalle) return;
    limpiarMensajes();
    const motivoLimpio = sanitizarMultilinea(motivo, 1000);
    const confirmacionLimpia = sanitizarTexto(confirmacion, 60);
    if (!motivoLimpio.trim()) {
      setError("El motivo de liberación es requerido.");
      return;
    }
    const bloqueo = validarSinInyeccion([{ etiqueta: "Motivo", valor: motivo }]);
    if (bloqueo) {
      const mensaje = `Bloqueado por seguridad: ${bloqueo}`;
      setError(mensaje);
      setLiberarError(mensaje);
      return;
    }
    setWorking(true);
    try {
      const data = await apiLicenciaLiberar({
        tenantId: detalle.tenantId || null,
        tenantActivationLicenseId: detalle.tenantActivationLicenseId,
        motivo: motivoLimpio,
        confirmacion: confirmacionLimpia,
        titularidadValidada,
        eliminarBaseFisica,
      });
      setOk(data?.mensaje || "Licencia liberada.");
      setLiberarError("");
      setDetalle(null);
      await buscar();
    } catch (err) {
      const mensaje = err.message || "No fue posible liberar la licencia.";
      setError(mensaje);
      setLiberarError(mensaje);
    } finally {
      setWorking(false);
    }
  };

  const columns = [
    { field: "licenseCode", headerName: "Licencia", width: 180 },
    {
      field: "licenseEstatus",
      headerName: "Estatus",
      width: 125,
      renderCell: (p) => <Chip size="small" label={p.value || "—"} color={p.value === "AVAILABLE" ? "success" : "warning"} />,
    },
    { field: "tenantKey", headerName: "TenantKey", width: 150 },
    { field: "razonSocial", headerName: "Razón social", width: 240, flex: 1 },
    { field: "databaseName", headerName: "Base actual", width: 160 },
    { field: "titularEmail", headerName: "Correo titular", width: 220 },
    {
      field: "impacto",
      headerName: "Equipos",
      width: 110,
      sortable: false,
      valueGetter: (value) => value?.equiposTenant || 0,
    },
    {
      field: "acciones",
      headerName: "Acciones",
      width: 120,
      sortable: false,
      renderCell: (p) => (
        <Button size="small" startIcon={<VisibilityIcon />} onClick={() => seleccionar(p.row.tenantActivationLicenseId)}>
          Ver
        </Button>
      ),
    },
  ];

  const impacto = detalle?.impacto || {};
  const confirmacionLiberacion = detalle?.codigoConfirmacion || "";
  const confirmacionLiberacionOk =
    !!confirmacionLiberacion && confirmacion.trim().toUpperCase() === confirmacionLiberacion.toUpperCase();
  const confirmacionPareceCodigoCorreo = /^\d{4,8}$/.test(confirmacion.trim());
  const puedeLiberar =
    !!detalle &&
    !!motivo.trim() &&
    titularidadValidada &&
    confirmacionLiberacionOk;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Liberación de licencia
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Busca una licencia o tenant, valida la titularidad del sistema y libera la licencia junto con sus equipos y datos de activación.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" onClose={() => setOk("")} sx={{ mb: 2 }}>
          {ok}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
            <TextField
              label="Buscar licencia, tenant, BD, titular, correo, RFC o equipo"
              size="small"
              fullWidth
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") buscar();
              }}
            />
            <Button variant="contained" startIcon={<SearchIcon />} onClick={buscar} disabled={loading || working}>
              Buscar
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ height: 360, mb: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.tenantActivationLicenseId}
          loading={loading}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </Box>

      {detalleLoading && <LinearProgress sx={{ mb: 2 }} />}

      {detalle && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Datos de titularidad y activación
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Licencia</Typography>
                    <Typography variant="body2">{show(detalle.licenseCode)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">LicenseGuid</Typography>
                    <Typography variant="body2">{show(detalle.licenseGuid)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Titular / razón social</Typography>
                    <Typography variant="body2">{show(detalle.razonSocial || detalle.issuedToName || detalle.titularNombre)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Correo para validar</Typography>
                    <Typography variant="body2">{show(detalle.correoValidacionSugerido)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">TenantKey</Typography>
                    <Typography variant="body2">{show(detalle.tenantKey)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Base actual</Typography>
                    <Typography variant="body2">{show(detalle.databaseName)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Usuario administrador</Typography>
                    <Typography variant="body2">{show(detalle.adminLoginName || detalle.titularEmail)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Activada / vencimiento</Typography>
                    <Typography variant="body2">{formatFecha(detalle.redeemedAtUtc)} / {formatFecha(detalle.expiresAtUtc)}</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Impacto de liberación
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <ImpactoItem label="Usuarios" value={impacto.usuariosTenant} color="primary" />
                  <ImpactoItem label="Equipos" value={impacto.equiposTenant} color="primary" />
                  <ImpactoItem label="Equipos activos" value={impacto.equiposActivos} color="warning" />
                  <ImpactoItem label="Solicitudes móviles" value={impacto.solicitudesMoviles} />
                  <ImpactoItem label="PrinterTools" value={impacto.printerTools} />
                  <ImpactoItem label="Redenciones" value={impacto.redenciones} />
                  <ImpactoItem label="Suscripciones" value={impacto.suscripciones} />
                  <ImpactoItem label="Licencias runtime" value={impacto.licenciasRuntime} />
                  <ImpactoItem label="Jobs" value={impacto.jobsProvisionamiento} />
                  <ImpactoItem label="Bloqueos" value={impacto.bloqueosEquipo} />
                  <ImpactoItem label="Intentos seguridad" value={impacto.intentosSeguridad} />
                  <ImpactoItem label="Licencias a liberar" value={impacto.licenciasActivacionLiberadas} color="error" />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Validar titularidad por correo
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Destinatario"
                      type="email"
                      size="small"
                      fullWidth
                      value={correoTo}
                      onChange={(e) => setCorreoTo(e.target.value)}
                      placeholder={detalle.correoValidacionSugerido || "correo@ejemplo.com"}
                    />
                    <TextField
                      label="Mensaje interno para el correo"
                      size="small"
                      multiline
                      rows={3}
                      fullWidth
                      value={correoMensaje}
                      onChange={(e) => setCorreoMensaje(e.target.value)}
                    />
                    <Button variant="outlined" startIcon={<MailOutlineIcon />} onClick={enviarCorreo} disabled={working}>
                      Enviar validación
                    </Button>
                    <Divider />
                    <TextField
                      label="Código recibido por correo"
                      size="small"
                      fullWidth
                      value={codigoValidacion}
                      onChange={(e) => setCodigoValidacion(e.target.value.replace(/\s+/g, ""))}
                      helperText={
                        titularidadValidada
                          ? `Titularidad verificada${detalle.ownershipVerifiedAtUtc ? `: ${formatFecha(detalle.ownershipVerifiedAtUtc)}` : ""}.`
                          : validacionExpiresAt
                            ? `El código expira: ${formatFecha(validacionExpiresAt)}.`
                            : "Envía la validación y captura aquí el código recibido por correo."
                      }
                      disabled={titularidadValidada || working}
                    />
                    <Button
                      variant="contained"
                      onClick={confirmarCodigo}
                      disabled={!codigoValidacion.trim() || titularidadValidada || working}
                    >
                      Confirmar código
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Acción sensible</AlertTitle>
                    La liberación elimina la relación del tenant con la licencia, equipos, usuarios y registros de activación. Primero valida la titularidad con el cliente.
                  </Alert>
                  <Stack spacing={2}>
                    <TextField
                      label="Motivo de liberación"
                      size="small"
                      multiline
                      rows={3}
                      fullWidth
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={titularidadValidada} disabled />}
                      label={titularidadValidada ? "Titularidad confirmada con código" : "Confirma primero el código enviado por correo"}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={eliminarBaseFisica} onChange={(e) => setEliminarBaseFisica(e.target.checked)} />}
                      label="Eliminar también la base física del tenant"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Confirmación requerida: <b>{detalle.codigoConfirmacion}</b>
                    </Typography>
                    {titularidadValidada && (
                      <Alert severity="info">
                        El código del correo ya quedó validado. Para habilitar la liberación escribe la frase completa:
                        <br />
                        <b>{detalle.codigoConfirmacion}</b>
                      </Alert>
                    )}
                    <TextField
                      label="Escribe la confirmación exacta de liberación"
                      size="small"
                      fullWidth
                      value={confirmacion}
                      onChange={(e) => setConfirmacion(e.target.value)}
                      error={Boolean(confirmacion.trim()) && !confirmacionLiberacionOk}
                      helperText={
                        confirmacionLiberacionOk
                          ? "Confirmación correcta. Ya puedes liberar."
                          : confirmacionPareceCodigoCorreo
                            ? "Ese es el código del correo; aquí escribe la frase LIBERAR completa."
                            : "Este campo confirma la acción destructiva; no es el código del correo."
                      }
                    />
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteForeverIcon />}
                      onClick={liberar}
                      disabled={!puedeLiberar || working}
                    >
                      Liberar licencia y equipos
                    </Button>
                    {liberarError && (
                      <Alert severity="error">
                        {liberarError}
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default LiberacionLicenciaPage;
